import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { deviceSpecsApi, serversApi } from '../../api'
import { getErrorMessage } from '../../api/client'
import type { DeviceSpecDevice, EdgeDevice, EdgeDeviceChannel, EdgeTag, EdgeTagEdit } from '../../types'
import { channelDef } from '../deviceSpec/catalog'
import { toEdgeChannelInfo } from '../deviceSpec/channelShape'
import { ComparePreview, PlanBadge as ComparePlanBadge } from '../deviceSpec/CompareView'
import { planOf } from '../deviceSpec/specCompare'
import type { PlanState } from '../deviceSpec/specCompare'
import { endpointText, validateSpec } from '../deviceSpec/model'
import { pairTags } from '../deviceSpec/pullMerge'

// 명세 → Edge 적용 — 디바이스 명세서의 디바이스를 골라 이 Edge 에 넣는다 (서버 상세 [디바이스] 탭).
//  · 같은 코드가 Edge 에 있으면 덮어쓰기(채널 설정 수정 + 태그 목록 교체), 없으면 추가
//  · 태그는 [Edge → 명세 가져오기] 와 같은 규칙으로 짝짓는다 (주소 표기 차이 무시 — pairTags). 짝이 맞으면 Id 를 이어받아
//    마스터데이터 연결이 유지된다. 명세에 없는 Edge 태그는 지워진다
//  · 태그 Alias 는 명세 값을 보낸다 (Edge ↔ 명세 양방향). 비교·미리보기는 가져오기와 같은 화면(CompareView)
//  · 태그 Description 은 건드리지 않는다 — 기존 태그면 Edge 값 그대로, 새 태그는 비움 (Edge 메모. 명세의 설명·항목명은 명세 전용)
//  · 하나씩 차례로 보내고, 실패해도 다음 디바이스로 넘어간다

type Plan = { kind: 'blocked'; reasons: string[] } | PlanState

type Result = { state: 'pending' | 'running' | 'done' | 'failed'; message?: string }

// 명세 태그 key → 짝지어진 Edge 태그 (Id 를 이어받는 짝)
function edgeTagsBySpec(spec: DeviceSpecDevice, existing: EdgeDevice | undefined): Map<string, EdgeTag> {
  const map = new Map<string, EdgeTag>()
  if (!existing) return map
  pairTags(spec.channelType, existing.tags, spec.tags).forEach((t, i) => { if (t) map.set(t.key, existing.tags[i]) })
  return map
}

function tagEdits(spec: DeviceSpecDevice, existing: EdgeDevice | undefined): EdgeTagEdit[] {
  const paired = edgeTagsBySpec(spec, existing)
  return spec.tags.map(t => {
    const same = paired.get(t.key)
    return {
      id: same?.id ?? null,
      address: t.address.trim(),
      size: Number(t.size) || 1,
      dataType: t.dataType || 'Word',
      arithmetic: t.arithmetic.trim() || null,
      description: same?.description ?? null,
      alias: t.alias.trim(),
    }
  })
}

export default function ApplySpecDialog({ serverId, initialSpecId, onSpecChange, edgeDevices, lockReason, onClose, onApplied }: {
  serverId: string
  initialSpecId: string | null // 이 서버에서 마지막으로 고른 명세서
  onSpecChange: (specId: string) => void
  edgeDevices: EdgeDevice[]
  lockReason: string | null
  onClose: () => void
  onApplied: (message: string, failed: boolean) => void
}) {
  const [specId, setSpecId] = useState(initialSpecId ?? '')
  const [checked, setChecked] = useState<Set<string>>(new Set())
  const [previewKey, setPreviewKey] = useState<string | null>(null)
  const [results, setResults] = useState<Record<string, Result>>({})
  const [running, setRunning] = useState(false)

  const closeRef = useRef(onClose)
  useEffect(() => { closeRef.current = onClose })
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !running) closeRef.current() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [running])

  const specsQuery = useQuery({
    queryKey: ['device-specs'],
    queryFn: () => deviceSpecsApi.getAll().then(r => r.data),
  })
  const specQuery = useQuery({
    queryKey: ['device-specs', specId],
    queryFn: () => deviceSpecsApi.get(specId).then(r => r.data),
    enabled: !!specId,
    staleTime: 0,
  })
  const devices = useMemo(() => specQuery.data?.devices ?? [], [specQuery.data])
  const edgeOf = (d: DeviceSpecDevice) => edgeDevices.find(e => e.code === d.code.trim())

  // Edge 에 같은 코드가 있는 디바이스만 채널 설정을 받아 접속 정보까지 견준다 (현장 규모 ≤20)
  const matchedCodes = useMemo(() => devices.map(d => d.code.trim()).filter(c => c && edgeDevices.some(e => e.code === c)), [devices, edgeDevices])
  const channelQueries = useQueries({
    queries: matchedCodes.map(code => ({
      queryKey: ['servers', serverId, 'devices', code, 'channel'],
      queryFn: () => serversApi.getDeviceChannel(serverId, code).then(r => r.data),
      retry: false,
      staleTime: 0,
    })),
  })
  const channels = new Map<string, EdgeDeviceChannel>()
  const channelFailed = new Set<string>()
  matchedCodes.forEach((code, i) => {
    const q = channelQueries[i]
    if (q?.data) channels.set(code, q.data)
    else if (q?.isError) channelFailed.add(code)
  })

  // 디바이스별 계획 — 보낼 수 없는 이유(점검), 아니면 Edge 와 견준 결과
  const issues = useMemo(() => validateSpec(devices), [devices])
  const plans: Record<string, Plan> = {}
  for (const d of devices) {
    const reasons: string[] = []
    const de = issues.device[d.key] ?? {}
    if (de.code) reasons.push(`디바이스 코드 ${de.code}`)
    if (de.name) reasons.push(`이름 ${de.name}`)
    if (de.channelType) reasons.push('프로토콜 선택 필요')
    if (de.endpoint) reasons.push(`접속 정보 ${de.endpoint}`)
    if (d.channelType === 'Custom' && !['DATABASE', 'TCP'].includes(d.channel.type || 'DATABASE')) reasons.push('Custom 은 DATABASE·TCP 만')
    const badTags = d.tags.filter(t => issues.tag[t.key]).length
    if (badTags > 0) reasons.push(`태그 ${badTags}개 확인 필요 (주소·크기·타입)`)
    if (d.tags.length === 0) reasons.push('태그 없음')
    const code = d.code.trim()
    plans[d.key] = reasons.length > 0 ? { kind: 'blocked', reasons }
      : planOf(d, edgeOf(d), channels.get(code), 'push', false, channelFailed.has(code))
  }
  const canSend = (d: DeviceSpecDevice) => { const k = plans[d.key]?.kind; return k !== 'blocked' && k !== 'same' }

  const sendable = devices.filter(canSend)
  const selected = devices.filter(d => checked.has(d.key) && canSend(d))
  const removing = selected.reduce((n, d) => { const p = plans[d.key]; return n + (p?.kind === 'update' ? p.summary.remove : 0) }, 0)
  const preview = devices.find(d => d.key === previewKey) ?? null

  const pickSpec = (id: string) => {
    setSpecId(id)
    if (id) onSpecChange(id)
    setChecked(new Set())
    setPreviewKey(null)
    setResults({})
  }

  const toggle = (key: string) => setChecked(s => {
    const next = new Set(s)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    return next
  })

  const apply = async () => {
    if (selected.length === 0 || running) return
    const msg = [`디바이스 ${selected.length}개를 이 Edge 에 적용합니다.`]
    if (removing > 0) msg.push(`Edge 태그 ${removing}개가 삭제되고, 그 태그를 쓰던 마스터데이터 항목 연결이 끊깁니다.`)
    if (!confirm(msg.join('\n'))) return
    setRunning(true)
    setResults(Object.fromEntries(selected.map(d => [d.key, { state: 'pending' } as Result])))
    let failed = 0
    for (const d of selected) {
      setResults(r => ({ ...r, [d.key]: { state: 'running' } }))
      const existing = edgeOf(d)
      const body = {
        channelType: d.channelType, code: d.code.trim(), name: d.name.trim(),
        description: d.description.trim() || null, channelInfoJson: JSON.stringify(toEdgeChannelInfo(d.channelType, d.channel)),
      }
      try {
        if (existing) await serversApi.updateDevice(serverId, body)
        else await serversApi.createDevice(serverId, body)
        await serversApi.updateDeviceTags(serverId, body.code, tagEdits(d, existing))
        setResults(r => ({ ...r, [d.key]: { state: 'done', message: existing ? '덮어씀' : '추가함' } }))
      } catch (err) {
        failed++
        setResults(r => ({ ...r, [d.key]: { state: 'failed', message: getErrorMessage(err, '적용하지 못했습니다.') } }))
      }
    }
    setRunning(false)
    onApplied(failed === 0
      ? `디바이스 ${selected.length}개를 적용했습니다.`
      : `${selected.length}개 중 ${failed}개를 적용하지 못했습니다. 목록에서 이유를 확인하세요.`, failed > 0)
  }

  const finished = Object.values(results).length > 0 && !running

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onMouseDown={() => { if (!running) onClose() }}>
      <div className="bg-surface rounded-xl shadow-xl w-full max-w-6xl h-[85vh] flex flex-col overflow-hidden" onMouseDown={e => e.stopPropagation()}>
        <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-gray-100 shrink-0">
          <h3 className="font-semibold text-gray-800">명세 → Edge 적용</h3>
          <select value={specId} onChange={e => pickSpec(e.target.value)} disabled={running}
            className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm min-w-[260px]">
            <option value="">{specsQuery.isLoading ? '불러오는 중…' : '명세서를 고르세요'}</option>
            {(specsQuery.data ?? []).map(s => (
              <option key={s.id} value={s.id}>{s.name}{s.company ? ` · ${s.company.name}` : ''} (디바이스 {s.deviceCount})</option>
            ))}
          </select>
          {specQuery.isFetching && <span className="text-xs text-gray-400">불러오는 중…</span>}
          <span className="ml-auto text-xs text-gray-400">명세 값으로 이 Edge(현장) 설정을 바꿉니다</span>
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
          {/* 디바이스 목록 */}
          <div className="min-h-0 overflow-auto border-r border-gray-100">
            {!specId ? (
              <p className="p-8 text-center text-sm text-gray-400">명세서를 고르면 디바이스가 나옵니다.</p>
            ) : devices.length === 0 && !specQuery.isFetching ? (
              <p className="p-8 text-center text-sm text-gray-400">이 명세서에는 디바이스가 없습니다.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs sticky top-0 z-10">
                  <tr>
                    <th className="w-10 px-3 py-2">
                      <input type="checkbox" disabled={running || sendable.length === 0}
                        checked={sendable.length > 0 && sendable.every(d => checked.has(d.key))}
                        onChange={e => setChecked(e.target.checked ? new Set(sendable.map(d => d.key)) : new Set())}
                        title="적용할 수 있는 디바이스 전체 (변경 없는 디바이스는 빼고)" />
                    </th>
                    <th className="text-left px-2 py-2">명세 디바이스</th>
                    <th className="text-left px-2 py-2">프로토콜 · 접속</th>
                    <th className="text-left px-2 py-2">적용하면 Edge 는</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {devices.map(d => {
                    const plan = plans[d.key]
                    const result = results[d.key]
                    const def = channelDef(d.channelType)
                    return (
                      <tr key={d.key} onClick={() => setPreviewKey(d.key)}
                        className={`cursor-pointer align-top ${d.key === previewKey ? 'bg-blue-50/60' : 'hover:bg-gray-50'} ${plan?.kind === 'same' && !result ? 'opacity-60' : ''}`}>
                        <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={checked.has(d.key) && canSend(d)} disabled={running || !canSend(d)}
                            onChange={() => toggle(d.key)} />
                        </td>
                        <td className="px-2 py-2.5">
                          <div className="font-medium text-gray-800">{d.name || '(이름 없음)'}</div>
                          <div className="text-xs text-gray-400 font-mono">{d.code || '코드 없음'} · 태그 {d.tags.length}</div>
                        </td>
                        <td className="px-2 py-2.5 text-xs text-gray-600">
                          <div>{def?.label ?? <span className="text-gray-400">미정</span>}</div>
                          {def && <div className="font-mono text-gray-500">{endpointText(d)}</div>}
                        </td>
                        <td className="px-2 py-2.5 text-xs">
                          {result ? <ResultBadge result={result} />
                            : plan?.kind === 'blocked' ? <BlockedBadge reasons={plan.reasons} />
                            : <ComparePlanBadge plan={plan} dir="push" />}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* 미리보기 — 명세 값, Edge 와 다른 칸은 빨강 */}
          <div className="min-h-0 overflow-auto">
            {preview
              ? <ComparePreview key={preview.key} dir="push" spec={preview} edge={edgeOf(preview)} channel={channels.get(preview.code.trim())} keepSpecOnly={false} />
              : <p className="p-8 text-center text-sm text-gray-400">디바이스를 누르면 Edge 와 다른 곳을 볼 수 있습니다.</p>}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-t border-gray-100 shrink-0">
          <span className="text-xs text-gray-500">
            {selected.length > 0 ? `${selected.length}개 선택` : '적용할 디바이스를 고르세요'}
            {removing > 0 && <span className="text-red-700 font-semibold"> · Edge 태그 {removing}개 삭제</span>}
          </span>
          {lockReason && <span className="text-xs text-red-600">{lockReason}</span>}
          <div className="ml-auto flex gap-2">
            <button onClick={onClose} disabled={running}
              className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition disabled:opacity-50">
              {finished ? '닫기' : '취소'}
            </button>
            <button onClick={apply} disabled={running || selected.length === 0 || !!lockReason}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-accent-hover transition disabled:opacity-50">
              {running ? '적용하는 중…' : `Edge 에 적용${selected.length ? ` (${selected.length})` : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function BlockedBadge({ reasons }: { reasons: string[] }) {
  return (
    <div>
      <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">적용할 수 없음</span>
      <ul className="mt-1 text-[11px] text-red-600 space-y-0.5">{reasons.map(r => <li key={r}>{r}</li>)}</ul>
    </div>
  )
}

function ResultBadge({ result }: { result: Result }) {
  const tone = { pending: 'bg-gray-100 text-gray-500', running: 'bg-blue-50 text-blue-700', done: 'bg-green-50 text-green-700', failed: 'bg-red-50 text-red-700' }[result.state]
  const label = { pending: '대기', running: '적용하는 중…', done: result.message ?? '완료', failed: '실패' }[result.state]
  return (
    <div>
      <span className={`px-1.5 py-0.5 rounded ${tone}`}>{label}</span>
      {result.state === 'failed' && result.message && <div className="mt-1 text-[11px] text-red-600">{result.message}</div>}
    </div>
  )
}
